/**
 * The list the composer opens above itself (below, when a centred box has no
 * room above: composer-menu-placement.ts): the "+" sources, the "@" mentions
 * and the "/" commands all render here, so they share one look, one gliding
 * highlight and one footer.
 *
 * The highlight is a single span that glides to the active row (220ms,
 * animating `top` and `height`) instead of every row toggling its own
 * background. The composer owns the keyboard: the input keeps focus and moves
 * `activeIndex`; this list only reports hover and clicks.
 */
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export type ComposerMenuRow = {
  key: string;
  leading?: ReactNode;
  title: string;
  /** Beside the title, before the description (a verified mark). */
  badge?: ReactNode;
  description?: string;
  /** The right slot: a state, a plan badge. */
  trailing?: ReactNode;
  /** Hover text for details that do not fit the row. */
  tooltip?: string;
  /** Emphasises the trailing slot (a control set to anything but Auto). */
  isSet?: boolean;
};

export function composerMenuRowId(menuId: string, index: number) {
  return `${menuId}-row-${index}`;
}

export function ComposerMenu({
  id,
  label,
  rows,
  activeIndex,
  showHighlight,
  onHover,
  onLeave,
  onPick,
  footer,
  emptyText,
  listStyle,
  compact = false,
  placement = 'above',
  maxHeight = null,
}: {
  id: string;
  label: string;
  rows: readonly ComposerMenuRow[];
  activeIndex: number;
  /** The highlight shows once a row is hovered or the arrows are used. */
  showHighlight: boolean;
  onHover: (index: number) => void;
  onLeave: () => void;
  onPick: (index: number) => void;
  footer: string;
  emptyText: string;
  listStyle?: CSSProperties;
  compact?: boolean;
  /** Which side of the box it opens on (composer-menu-placement.ts). */
  placement?: 'above' | 'below';
  /** A height cap in px when neither side fits the whole menu. */
  maxHeight?: number | null;
}) {
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [box, setBox] = useState<{ top: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const row = rowRefs.current[activeIndex];
    if (!row) {
      setBox(null);
      return;
    }
    setBox({ top: row.offsetTop, height: row.offsetHeight });
    // Keyboard movement through a long list keeps the active row in view.
    if (showHighlight) row.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, rows, showHighlight]);

  return (
    <div
      className={[
        'tabbio-composer-menu',
        compact ? 'is-source-menu' : null,
        placement === 'below' ? 'is-below' : null,
      ]
        .filter(Boolean)
        .join(' ')}
      style={maxHeight != null ? { maxHeight, overflowY: 'auto' } : undefined}
      // The input keeps focus: a click on the menu must not blur it.
      onMouseDown={(event) => event.preventDefault()}
      onMouseLeave={onLeave}
    >
      <div
        id={id}
        role="listbox"
        aria-label={label}
        className="tabbio-composer-menu-list"
        style={listStyle}
      >
        <span
          aria-hidden
          className="tabbio-composer-menu-glide"
          style={{
            top: box?.top ?? 0,
            height: box?.height ?? 0,
            opacity: box && showHighlight && rows.length > 0 ? 1 : 0,
          }}
        />
        {rows.map((row, index) => (
          <button
            key={row.key}
            ref={(element) => {
              rowRefs.current[index] = element;
            }}
            id={composerMenuRowId(id, index)}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            tabIndex={-1}
            title={row.tooltip}
            className="tabbio-composer-menu-row"
            onMouseEnter={() => onHover(index)}
            onClick={() => onPick(index)}
          >
            {row.leading ? (
              <span className="tabbio-composer-menu-icon">{row.leading}</span>
            ) : null}
            <span className="tabbio-composer-menu-title">{row.title}</span>
            {row.badge ? (
              <span className="tabbio-composer-menu-badge">{row.badge}</span>
            ) : null}
            <span className="tabbio-composer-menu-desc">{row.description}</span>
            {row.trailing ? (
              <span
                className={
                  row.isSet
                    ? 'tabbio-composer-menu-trailing is-set'
                    : 'tabbio-composer-menu-trailing'
                }
              >
                {row.trailing}
              </span>
            ) : null}
          </button>
        ))}
        {rows.length === 0 ? (
          <div className="tabbio-composer-menu-empty">{emptyText}</div>
        ) : null}
      </div>
      <div className="tabbio-composer-menu-footer">{footer}</div>
    </div>
  );
}
